import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, ArrowLeft, FileSignature, CheckCircle2, CreditCard, XCircle, Ban, Upload, FileText, Trash2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DocumentsSection({ leaseId }) {
  const { user } = useContext(AuthContext);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('verification_form');
  const fileRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/documents/lease/${leaseId}`);
      setDocs(r.data);
    } catch (e) { log.error('DocumentsSection: fetch failed', e); }
  }, [leaseId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/documents?doc_type=${docType}&related_to_type=lease&related_to_id=${leaseId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success('Document(s) uploaded');
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      fetchDocs();
    } catch (e) {
      log.error('DocumentsSection: delete failed', e);
      toast.error('Delete failed');
    }
  };

  return (
    <Card className="p-6 mb-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Documents & Verification Forms ({docs.length})</h3>
        <div className="flex items-center gap-2">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger data-testid="doc-type-select" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="verification_form">Verification Form</SelectItem>
              <SelectItem value="id_proof">ID Proof</SelectItem>
              <SelectItem value="income_proof">Income Proof</SelectItem>
              <SelectItem value="lease_template">Lease Template</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" data-testid="doc-file-input"
            accept=".pdf,image/*,.doc,.docx,.txt" multiple onChange={handleUpload} className="hidden" />
          <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" className="rounded-full">
            <Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
      {docs.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground text-sm border border-dashed rounded-lg">
          No documents uploaded. Upload IDs, income proof, references, or verification forms.
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d, idx) => (
            <div key={d.id} data-testid={`doc-${idx}`} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{d.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.doc_type} • {d.uploader_name} • {d.uploader_role} • {new Date(d.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a href={`${BACKEND_URL}/api/files/${d.storage_path}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="rounded-full"><ExternalLink className="w-4 h-4" /></Button>
                </a>
                {d.uploader_id === user?.id && (
                  <Button data-testid={`doc-delete-${idx}-btn`} onClick={() => handleDelete(d.id)} variant="ghost" size="sm" className="rounded-full text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function LeaseDetailPage() {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [landlordSignature, setLandlordSignature] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [screening, setScreening] = useState(null);
  const [searchParams] = useSearchParams();

  const fetchLease = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLease(response.data);
    } catch (error) {
      toast.error('Lease not found');
      navigate('/leases');
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  const fetchScreening = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leases/${id}/screening`);
      setScreening(response.data);
    } catch (err) {
      // Tenants don't have access to screening data (403) — silently skip.
      // For other unexpected errors log for debugging.
      if (err.response?.status !== 403) {
        log.debug('fetchScreening: unexpected error', err);
      }
    }
  }, [id]);

  const handlePayRent = useCallback(async (dueDate) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/rent-checkout`, {
        lease_id: id,
        due_date: dueDate,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rent payment');
      setProcessing(false);
    }
  }, [id, token]);

  useEffect(() => { fetchLease(); }, [fetchLease]);

  useEffect(() => {
    // Only fetch screening for landlord/admin viewing a lease WITH application data
    if (lease && lease.application_data && (user?.role === 'landlord' || user?.role === 'admin' || user?.role === 'employee')) {
      fetchScreening();
    }
  }, [lease, user, fetchScreening]);

  useEffect(() => {
    // Auto-trigger rent checkout from email deep-link: /leases/{id}?pay_rent=1&due_date=YYYY-MM-DD
    if (lease && searchParams.get('pay_rent') === '1' && user?.role === 'tenant' && lease.status === 'active') {
      const dueDate = searchParams.get('due_date') || new Date().toISOString().slice(0, 10);
      handlePayRent(dueDate);
    }
  }, [lease, searchParams, user, handlePayRent]);

  const handleLandlordSign = async () => {
    if (!landlordSignature.trim()) { toast.error('Please type your name to sign'); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/leases/${id}/sign`, { landlord_signature: landlordSignature }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lease signed successfully!');
      fetchLease();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign lease');
    } finally { setProcessing(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this lease?')) return;
    try {
      await axios.post(`${API}/leases/${id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lease cancelled');
      fetchLease();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handlePayNow = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        property_id: lease.property_id,
        lease_months: lease.lease_months,
        origin_url: window.location.origin,
        lease_id: lease.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  const handlePayDeposit = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/deposit-checkout`, {
        property_id: lease.property_id,
        lease_months: lease.lease_months,
        origin_url: window.location.origin,
        lease_id: lease.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate deposit payment');
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/leases/${id}/reject`, { reason: rejectReason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lease rejected');
      setShowReject(false);
      fetchLease();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally { setProcessing(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  if (!lease) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate('/leases')} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <FileSignature className="w-8 h-8 text-primary" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Lease Agreement</h1>
          </div>
          <p className="text-muted-foreground">{lease.property_title}</p>
        </div>

        <Card className="p-8 mb-6 border border-border shadow-sm">
          <div className="grid grid-cols-2 gap-6">
            <div><div className="text-sm text-muted-foreground">Tenant</div><div className="font-medium mt-1">{lease.tenant_name}</div></div>
            <div><div className="text-sm text-muted-foreground">Landlord</div><div className="font-medium mt-1">{lease.landlord_name}</div></div>
            <div><div className="text-sm text-muted-foreground">Lease Duration</div><div className="font-medium mt-1">{lease.lease_months} months</div></div>
            <div><div className="text-sm text-muted-foreground">Monthly Rent</div><div className="font-medium text-primary mt-1">${lease.monthly_rent.toFixed(2)}</div></div>
            <div>
              <div className="text-sm text-muted-foreground">Security Deposit</div>
              <div className="font-medium mt-1 flex items-center gap-2">
                ${lease.deposit.toFixed(2)}
                {lease.deposit_paid ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Paid</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Paid separately</span>
                )}
              </div>
            </div>
            {lease.application_fee > 0 && (
              <div><div className="text-sm text-muted-foreground">Application Fee</div><div className="font-medium mt-1">${lease.application_fee.toFixed(2)}</div></div>
            )}
            {lease.down_payment > 0 && (
              <>
                <div><div className="text-sm text-muted-foreground">Down Payment</div><div className="font-medium mt-1">${lease.down_payment.toFixed(2)}</div></div>
              </>
            )}
            {lease.platform_fee > 0 && (
              <div><div className="text-sm text-muted-foreground">Platform Fees</div><div className="font-medium text-accent mt-1">${lease.platform_fee.toFixed(2)}</div></div>
            )}
            <div className="col-span-2 border-t border-border pt-4">
              <div className="text-sm text-muted-foreground">Total Due at Booking (first month + fees, deposit paid separately)</div>
              <div className="font-bold text-2xl text-accent mt-1">${(lease.monthly_rent + (lease.down_payment || 0) + (lease.application_fee || 0) + (lease.platform_fee || 0)).toFixed(2)}</div>
            </div>
          </div>
        </Card>

        {screening && screening.has_application && (
          <Card data-testid="screening-widget" className="p-8 mb-6 border border-border shadow-sm">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-medium">Screening Snapshot</h2>
                <p className="text-xs text-muted-foreground mt-1">Auto-computed from the applicant's data. Use this to decide faster.</p>
              </div>
              <div
                data-testid="screening-overall-tier"
                className={`text-right px-4 py-2 rounded-2xl border font-semibold text-sm ${
                  screening.overall.tier === 'green' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                  screening.overall.tier === 'yellow' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                  'bg-red-50 text-red-800 border-red-200'
                }`}
              >
                <div className="text-2xl font-bold leading-none">{screening.overall.score}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1">Overall · {screening.overall.tier}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                data-testid="screening-rti"
                className={`p-4 rounded-xl border ${
                  screening.rent_to_income.tier === 'green' ? 'bg-emerald-50 border-emerald-200' :
                  screening.rent_to_income.tier === 'yellow' ? 'bg-amber-50 border-amber-200' :
                  screening.rent_to_income.tier === 'red' ? 'bg-red-50 border-red-200' :
                  'bg-muted border-border'
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rent to Income</div>
                <div className="text-2xl font-bold mt-1 tabular-nums">
                  {screening.rent_to_income.pct != null ? `${screening.rent_to_income.pct}%` : '—'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  ${screening.rent_to_income.monthly_rent.toLocaleString()} rent / ${screening.rent_to_income.monthly_income.toLocaleString()} income
                </div>
              </div>
              <div
                data-testid="screening-employment"
                className={`p-4 rounded-xl border ${
                  screening.employment.tier === 'green' ? 'bg-emerald-50 border-emerald-200' :
                  screening.employment.tier === 'yellow' ? 'bg-amber-50 border-amber-200' :
                  screening.employment.tier === 'red' ? 'bg-red-50 border-red-200' :
                  'bg-muted border-border'
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Employment</div>
                <div className="text-2xl font-bold mt-1 tabular-nums">
                  {screening.employment.months != null ? `${(screening.employment.months / 12).toFixed(1)} yrs` : '—'}
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {screening.employment.employer || 'Not provided'}
                  {screening.employment.job_title ? ` · ${screening.employment.job_title}` : ''}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="screening-references">
              {[
                ['Personal ref', screening.references.has_personal_reference],
                ['Current landlord', screening.references.has_current_landlord_reference],
                ['Emergency contact', screening.references.has_emergency_contact],
                ['BG check consent', screening.references.background_check_consented],
              ].map(([label, ok]) => (
                <span
                  key={label}
                  className={`text-[11px] px-3 py-1 rounded-full font-medium ${
                    ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {ok ? '✓ ' : '✕ '}{label}
                </span>
              ))}
            </div>
          </Card>
        )}

        {lease.application_data && Object.keys(lease.application_data).length > 0 && (
          <Card className="p-8 mb-6 border border-border shadow-sm" data-testid="rental-application-card">
            <h2 className="text-xl font-medium mb-4">Rental Application</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              {[
                ['Full Legal Name', 'full_legal_name'],
                ['Date of Birth', 'date_of_birth'],
                ['Current Address', 'current_address'],
                ['Employer', 'employer'],
                ['Job Title', 'job_title'],
                ['Monthly Income', 'monthly_income'],
                ['Employed Since', 'employment_since'],
                ['Current Landlord', 'current_landlord_name'],
                ['Current Landlord Phone', 'current_landlord_phone'],
                ['Reason for Moving', 'reason_for_moving'],
                ['Reference', 'reference_name'],
                ['Reference Relationship', 'reference_relationship'],
                ['Reference Phone', 'reference_phone'],
                ['Emergency Contact', 'emergency_contact_name'],
                ['Emergency Contact Phone', 'emergency_contact_phone'],
              ].map(([label, key]) => {
                const v = lease.application_data[key];
                if (v == null || v === '' || v === 0) return null;
                const display = key === 'monthly_income' ? `$${Number(v).toLocaleString()}` : String(v);
                return (
                  <div key={key} data-testid={`application-field-${key}`}>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
                    <div className="mt-1 font-medium">{display}</div>
                  </div>
                );
              })}
            </div>
            {lease.application_data.consent_background_check && (
              <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Applicant consented to a background and credit check.
              </div>
            )}
          </Card>
        )}

        <Card className="p-8 mb-6 border border-border shadow-sm">
          <h2 className="text-xl font-medium mb-4">Lease Terms</h2>
          <pre data-testid="lease-terms-text" className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed font-sans">{lease.terms}</pre>
        </Card>

        <Card className="p-8 mb-6 border border-border shadow-sm">
          <h2 className="text-xl font-medium mb-6">Signatures</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tenant Signature</Label>
              <div className="mt-2 p-4 border-b-2 border-primary">
                <div className="font-serif italic text-xl" data-testid="tenant-signature-display">{lease.tenant_signature || '—'}</div>
              </div>
              {lease.tenant_signed_at && (
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                  Signed {new Date(lease.tenant_signed_at).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Landlord Signature</Label>
              {lease.landlord_signature ? (
                <>
                  <div className="mt-2 p-4 border-b-2 border-primary">
                    <div className="font-serif italic text-xl" data-testid="landlord-signature-display">{lease.landlord_signature}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                    Signed {new Date(lease.landlord_signed_at).toLocaleString()}
                  </div>
                </>
              ) : ((user?.role === 'landlord' && lease.landlord_id === user.id) || (user?.role === 'employee' && lease.landlord_id === user.parent_landlord_id && (user.permissions || []).includes('approve_leases'))) && lease.status === 'pending_landlord' ? (
                <div className="mt-2 space-y-3">
                  <Input data-testid="landlord-signature-input" placeholder="Type your full name to sign"
                    value={landlordSignature} onChange={(e) => setLandlordSignature(e.target.value)} className="font-serif italic" />
                  <div className="flex gap-2">
                    <Button data-testid="landlord-sign-btn" onClick={handleLandlordSign} disabled={processing}
                      className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                      <FileSignature className="w-4 h-4 mr-2" />Approve & Sign
                    </Button>
                    <Button data-testid="landlord-reject-btn" onClick={() => setShowReject(true)} disabled={processing} variant="outline"
                      className="rounded-full text-destructive border-destructive">
                      <Ban className="w-4 h-4 mr-2" />Reject
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 p-4 border-b-2 border-muted text-muted-foreground">Awaiting signature...</div>
              )}
            </div>
          </div>
        </Card>

        {/* Documents & Verification */}
        <DocumentsSection leaseId={id} />

        {user?.role === 'tenant' && lease.tenant_id === user.id && lease.status === 'active' && lease.payment_status !== 'paid' && (
          <Card className="p-6 mb-6 bg-accent/5 border-accent/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-medium text-lg">Ready to complete your booking?</h3>
                <p className="text-sm text-muted-foreground mt-1">Pay the first month rent + fees to activate your lease. Security deposit is paid separately.</p>
              </div>
              <Button data-testid="pay-now-btn" onClick={handlePayNow} disabled={processing} size="lg"
                className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground btn-hover">
                <CreditCard className="w-4 h-4 mr-2" />
                {processing ? 'Processing...' : `Pay $${(lease.monthly_rent + (lease.down_payment || 0) + (lease.application_fee || 0) + (lease.platform_fee || 0)).toFixed(2)}`}
              </Button>
            </div>
          </Card>
        )}

        {user?.role === 'tenant' && lease.tenant_id === user.id && lease.payment_status === 'paid' && lease.deposit > 0 && !lease.deposit_paid && (
          <Card className="p-6 mb-6 bg-primary/5 border-primary/20" data-testid="deposit-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-medium text-lg">Security Deposit</h3>
                <p className="text-sm text-muted-foreground mt-1">Pay the refundable security deposit of ${lease.deposit.toFixed(2)}. Returned at end of lease pending inspection.</p>
              </div>
              <Button data-testid="pay-deposit-btn" onClick={handlePayDeposit} disabled={processing} size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                <CreditCard className="w-4 h-4 mr-2" />
                {processing ? 'Processing...' : `Pay Deposit $${lease.deposit.toFixed(2)}`}
              </Button>
            </div>
          </Card>
        )}

        {lease.deposit_paid && (
          <Card className="p-4 mb-6 bg-green-50 border-green-200" data-testid="deposit-paid-badge">
            <div className="flex items-center gap-2 text-green-800 text-sm">
              <CreditCard className="w-4 h-4" /> Security deposit paid — refundable at end of lease
            </div>
          </Card>
        )}

        {lease.status !== 'cancelled' && lease.payment_status !== 'paid' && (
          <div className="flex justify-end">
            <Button data-testid="cancel-lease-btn" onClick={handleCancel} variant="outline" className="rounded-full text-destructive border-destructive">
              <XCircle className="w-4 h-4 mr-2" />Cancel Lease
            </Button>
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Lease Request</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea data-testid="reject-reason-textarea" value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="Let the tenant know why..." className="mt-2" />
            </div>
            <div className="flex gap-3">
              <Button data-testid="reject-confirm-btn" onClick={handleReject} disabled={processing} variant="destructive" className="flex-1 rounded-full">
                Confirm Rejection
              </Button>
              <Button onClick={() => setShowReject(false)} variant="outline" className="flex-1 rounded-full">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
